import { FC, useState } from 'react';
import { useProfile } from '@/hooks/useProfile';
import { Book, Filter, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MaterialCard } from '@/components/library/MaterialCard';
import { MaterialGrid } from '@/components/library/MaterialGrid';
import { useDigitalMaterials } from '@/hooks/useDigitalMaterials';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';

const DigitalLibrary: FC = () => {
  const { userProfile } = useProfile();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');
  const { materials, isLoading } = useDigitalMaterials();

  const filteredMaterials = materials?.filter(material => {
    const matchesSearch = 
      material.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      material.description.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || material.category === selectedCategory;
    const matchesType = selectedType === 'all' || material.type === selectedType;

    return matchesSearch && matchesCategory && matchesType;
  });

  // Obtener categorías únicas de los materiales
  const categories = Array.from(new Set(materials?.map(m => m.category) || [])).filter(Boolean);
  const types = Array.from(new Set(materials?.map(m => m.type) || [])).filter(Boolean);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <div className="container mx-auto px-4 py-8 max-w-[1400px]">
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-3 mb-8"
        >
          <Book className="w-8 h-8 text-primary animate-pulse" />
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            Biblioteca Digital
          </h1>
        </motion.div>

        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Buscar materiales..."
              className="pl-10 w-full"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Categoría" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las categorías</SelectItem>
                {categories.map(category => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                <SelectItem value="ARTICULO">Artículo</SelectItem>
                <SelectItem value="PDF">PDF</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {!userProfile?.isPremium && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8">
            <p className="text-yellow-800">
              Algunos materiales solo están disponibles para usuarios Premium. 
              Actualiza tu plan desde el botón en el menú superior para acceder a todo el contenido.
            </p>
          </div>
        )}

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-[300px] rounded-lg" />
            ))}
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center mb-4">
              <div className="flex gap-2 items-center">
                <span className="text-gray-600">
                  {filteredMaterials?.length} materiales encontrados
                </span>
                {selectedCategory !== 'all' && (
                  <Badge variant="secondary" className="ml-2">
                    {selectedCategory}
                  </Badge>
                )}
                {selectedType !== 'all' && (
                  <Badge variant="secondary" className="ml-2">
                    {selectedType}
                  </Badge>
                )}
              </div>
            </div>
            <MaterialGrid>
              {filteredMaterials?.map((material) => (
                <MaterialCard 
                  key={material.id} 
                  material={material}
                />
              ))}
            </MaterialGrid>
          </>
        )}
      </div>
    </div>
  );
};

export default DigitalLibrary;
